import React from 'react'
import { getSocket } from '../lib/websockets'
import {
  TASK_UPDATED,
  TASK_CREATED,
  EVIDENCE_CREATED,
  EVIDENCE_UPDATED
} from '../lib/websockets/constants'
import { useAuthContext } from '../context/AuthProvider'
import { useAppContext } from '../context/AppProvider'
import { useHistory } from 'react-router-dom'
import { pathNameExists } from 'utils'

const allowedPaths = ['/auth/', '/auth', '/auth/tasks']

const LIVE_EVENTS = [TASK_UPDATED, TASK_CREATED, EVIDENCE_CREATED, EVIDENCE_UPDATED]

/**
 * Refetch the caller's data whenever the server reports task/evidence activity.
 *
 * Two problems this version fixes:
 *  - The handlers were registered but never removed. Every dependency change
 *    re-ran the effect and added another copy of all four listeners to the same
 *    socket, so a page left open long enough fired N refetches per event and
 *    leaked the closures behind them.
 *  - `callback` was read from the first render's closure. A refetch registered
 *    on mount kept calling the stale version for the life of the component.
 */
const useTaskSocketEvens = (callback?: () => Promise<void>) => {
  const { isAuthenticated } = useAuthContext()
  const { setIsLive, connected } = useAppContext()
  const socket = getSocket()
  const history = useHistory()
  const isOnLocation = pathNameExists(history.location.pathname, allowedPaths)

  // Keep the latest callback reachable without re-subscribing for it.
  const callbackRef = React.useRef(callback)
  React.useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  React.useEffect(() => {
    if (!socket || !isAuthenticated || !connected || !isOnLocation) return

    const refetchData = () => {
      void callbackRef.current?.()
    }

    LIVE_EVENTS.forEach((event) => socket.on(event, refetchData))
    setIsLive?.(socket.connected)

    return () => {
      LIVE_EVENTS.forEach((event) => socket.off(event, refetchData))
    }
  }, [socket, isAuthenticated, connected, isOnLocation, setIsLive])
}

export default useTaskSocketEvens
